import React, { useState, useEffect } from "react";
import { X, Save, Package, Layers, Tag, Plus, Trash2, Truck } from "lucide-react";
import { API_BASE_URL } from "../../utils/apiConfig";

interface SingleProduct {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

interface BundleComponent {
  productId: string;
  productName: string;
  quantity: number;
  price: number;
}

interface EditProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  productId?: string;
}

const EditProductModal: React.FC<EditProductModalProps> = ({
  isOpen,
  onClose,
  onSave,
  productId,
}) => {
  const [formData, setFormData] = useState({
    product_name: "",
    sku: "",
    model: "",
    price: "",
    salePrice: "",
    currency: "RSD",
    link: "",
    main_image: "",
    type: "single" as "single" | "bundle",
    freeShipping: false,
  });
  const [bundleComponents, setBundleComponents] = useState<BundleComponent[]>([]);
  const [singleProducts, setSingleProducts] = useState<SingleProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSingleProducts, setLoadingSingleProducts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (productId) {
        loadProduct();
      }
      loadSingleProducts();
    }
  }, [isOpen, productId]);

  // Calculate total price from bundle components
  const calculateTotalPrice = () => {
    if (formData.type !== "bundle" || bundleComponents.length === 0) {
      return 0;
    }
    return bundleComponents.reduce((total, component) => {
      return total + (component.price * component.quantity);
    }, 0);
  };

  const totalPrice = calculateTotalPrice();
  const salePrice = formData.salePrice ? parseFloat(formData.salePrice) : totalPrice;

  const loadSingleProducts = async () => {
    try {
      setLoadingSingleProducts(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/products/single`, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setSingleProducts(data.data.products || []);
      }
    } catch (err) {
      console.error("Error loading single products:", err);
    } finally {
      setLoadingSingleProducts(false);
    }
  };

  const loadProduct = async () => {
    if (!productId) return;

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/api/products/${productId}`, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data.product) {
        const product = data.data.product;
        setFormData({
          product_name: product.product_name || "",
          sku: product.sku || "",
          model: product.model || "",
          price: product.price_rsd || product.price_mkd || "",
          salePrice: product.salePrice || "",
          currency: product.currency || "RSD",
          link: product.link || "",
          main_image: product.main_image || "",
          type: product.type || "single",
          freeShipping: Boolean(product.freeShipping),
        });
        
        // Load bundle components if exists
        if (product.bundleComponents && product.bundleComponents.length > 0) {
          setBundleComponents(product.bundleComponents);
        }
      }
    } catch (err) {
      console.error("Error loading product:", err);
      setError(err instanceof Error ? err.message : "Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem("authToken");
      const url = productId
        ? `${API_BASE_URL}/api/products/${productId}`
        : `${API_BASE_URL}/api/products`;
      const method = productId ? "PUT" : "POST";

      const payload: any = {
        product_name: formData.product_name.trim(),
        sku: formData.sku.trim() || null,
        model: formData.model.trim() || null,
        currency: formData.currency,
        link: formData.link.trim() || null,
        main_image: formData.main_image.trim() || null,
        type: formData.type,
        bundleComponents: formData.type === "bundle" ? bundleComponents : [],
        salePrice: formData.salePrice ? parseFloat(formData.salePrice) : null,
        freeShipping: formData.freeShipping,
      };
      
      // Set price based on type
      if (formData.type === "single") {
        const price = parseFloat(formData.price.toString());
        if (formData.currency === "RSD") {
          payload.price_rsd = price;
        } else if (formData.currency === "MKD") {
          payload.price_mkd = price;
        } else if (formData.currency === "EUR") {
          payload.price_eur = price;
        }
      } else {
        // For bundles, calculated price is stored but sale price can override
        payload.calculatedPrice = totalPrice;
        if (formData.currency === "RSD") {
          payload.price_rsd = salePrice;
        } else if (formData.currency === "MKD") {
          payload.price_mkd = salePrice;
        } else if (formData.currency === "EUR") {
          payload.price_eur = salePrice;
        }
      }

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to save product");
      }

      const data = await response.json();
      if (data.success) {
        onSave();
      } else {
        throw new Error(data.message || "Failed to save product");
      }
    } catch (err) {
      console.error("Error saving product:", err);
      setError(err instanceof Error ? err.message : "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const addBundleComponent = (productId: string, quantity: number) => {
    const product = singleProducts.find((p) => p.id === productId);
    if (!product) return;

    setBundleComponents([
      ...bundleComponents,
      {
        productId: product.id,
        productName: product.name,
        quantity,
        price: product.price,
      },
    ]);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      style={{ marginTop: 0 }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {productId ? "Edit Product" : "Add Product"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#264a3e] mx-auto"></div>
            <p className="text-sm text-gray-600 mt-2">Loading product...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.product_name}
                onChange={(e) =>
                  setFormData({ ...formData, product_name: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                placeholder="e.g., Curcumactiv (500ml)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SKU
                </label>
                <input
                  type="text"
                  value={formData.sku}
                  onChange={(e) =>
                    setFormData({ ...formData, sku: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                  placeholder="SKU-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model
                </label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) =>
                    setFormData({ ...formData, model: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                  placeholder="MODEL-001"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {formData.type === "single" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price <span className="text-red-500">*</span>
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="number"
                      required
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                      placeholder="2990"
                    />
                    <select
                      value={formData.currency}
                      onChange={(e) =>
                        setFormData({ ...formData, currency: e.target.value })
                      }
                      className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                    >
                      <option value="RSD">RSD</option>
                      <option value="EUR">EUR</option>
                      <option value="MKD">MKD</option>
                    </select>
                  </div>
                </div>
              )}
              
              {formData.type === "bundle" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) =>
                      setFormData({ ...formData, currency: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                  >
                    <option value="RSD">RSD</option>
                    <option value="EUR">EUR</option>
                    <option value="MKD">MKD</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Price calculated from components
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Type
                </label>
                <div className="flex space-x-4 pt-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="single"
                      checked={formData.type === "single"}
                      onChange={(e) =>
                        setFormData({ ...formData, type: "single" })
                      }
                      className="mr-2"
                      style={{ accentColor: "#264a3e" }}
                    />
                    <Tag className="w-4 h-4 mr-1 text-green-600" />
                    <span className="text-sm">Single</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="bundle"
                      checked={formData.type === "bundle"}
                      onChange={(e) =>
                        setFormData({ ...formData, type: "bundle" })
                      }
                      className="mr-2"
                      style={{ accentColor: "#264a3e" }}
                    />
                    <Layers className="w-4 h-4 mr-1 text-blue-600" />
                    <span className="text-sm">Bundle</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Single: Single product. Bundle: Combination of products.
                </p>
              </div>
            </div>

            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.freeShipping}
                  onChange={(e) =>
                    setFormData({ ...formData, freeShipping: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-gray-300 text-[#264a3e] focus:ring-[#264a3e]"
                />
                <Truck className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Free shipping</span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Link
              </label>
              <input
                type="url"
                value={formData.link}
                onChange={(e) =>
                  setFormData({ ...formData, link: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                placeholder="https://naturatherapy.rs/product-name/"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image URL
              </label>
              <input
                type="url"
                value={formData.main_image}
                onChange={(e) =>
                  setFormData({ ...formData, main_image: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                placeholder="https://naturatherapy.rs/wp-content/uploads/..."
              />
              {formData.main_image && (
                <div className="mt-2">
                  <img
                    src={formData.main_image}
                    alt="Product preview"
                    className="h-24 w-24 object-cover rounded border border-gray-200"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>

            {formData.type === "bundle" && (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <h3 className="text-sm font-medium text-blue-900 mb-3">
                    Bundle Components
                  </h3>
                  
                  {bundleComponents.length === 0 ? (
                    <p className="text-xs text-blue-800 mb-3">
                      No components added yet. Add atomic products to create this bundle.
                    </p>
                  ) : (
                    <div className="space-y-2 mb-3">
                      {bundleComponents.map((component, index) => (
                        <div
                          key={index}
                          className="bg-white rounded p-2 flex items-center justify-between"
                        >
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {component.productName}
                            </div>
                            <div className="text-xs text-gray-600">
                              Quantity: {component.quantity} × {component.price} {formData.currency} = {component.quantity * component.price} {formData.currency}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setBundleComponents(
                                bundleComponents.filter((_, i) => i !== index)
                              );
                            }}
                            className="text-red-600 hover:text-red-800 ml-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      
                      <div className="border-t border-blue-300 pt-2 mt-2">
                        <div className="text-sm font-medium text-blue-900">
                          Calculated Total: {totalPrice} {formData.currency}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Add Component Form */}
                  <div className="border-t border-blue-300 pt-3">
                    <AddComponentForm
                      singleProducts={singleProducts}
                      loading={loadingSingleProducts}
                      onAdd={addBundleComponent}
                    />
                  </div>
                </div>
                
                {/* Pricing Summary */}
                <div className="bg-gray-50 border border-gray-300 rounded-md p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">
                    Bundle Pricing
                  </h4>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total (Calculated):</span>
                      <span className="font-medium text-gray-900">
                        {totalPrice.toFixed(2)} {formData.currency}
                      </span>
                    </div>
                    
                    <div className="border-t border-gray-300 pt-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sale Price <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        required
                        step="0.01"
                        min="0"
                        value={formData.salePrice}
                        onChange={(e) =>
                          setFormData({ ...formData, salePrice: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
                        placeholder={`Default: ${totalPrice.toFixed(2)}`}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the final sale price for this bundle.
                        {salePrice < totalPrice && salePrice > 0 && (
                          <span className="text-green-600 font-medium ml-1">
                            (Discount: {((1 - salePrice / totalPrice) * 100).toFixed(1)}%)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {formData.type === "single" && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-xs text-blue-800">
                  <strong>Quantity Management:</strong> Product quantities are managed separately
                  in the "Quantity" tab. This form only updates product information.
                </p>
              </div>
            )}
            
            {formData.type === "bundle" && bundleComponents.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> Add at least one single product to create a bundle.
                  Bundle quantities are determined by the single product availability.
                </p>
              </div>
            )}

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1a3329] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {productId ? "Update Product" : "Create Product"}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// Sub-component for adding bundle components
interface AddComponentFormProps {
  singleProducts: SingleProduct[];
  loading: boolean;
  onAdd: (productId: string, quantity: number) => void;
}

const AddComponentForm: React.FC<AddComponentFormProps> = ({
  singleProducts,
  loading,
  onAdd,
}) => {
  const [selectedProductId, setSelectedProductId] = useState("");
  const [quantity, setQuantity] = useState("1");

  const handleAdd = () => {
    if (!selectedProductId || !quantity) return;
    const qty = parseInt(quantity);
    if (isNaN(qty) || qty <= 0) return;

    onAdd(selectedProductId, qty);
    setSelectedProductId("");
    setQuantity("1");
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-blue-900">
        Add Component
      </label>
      <div className="flex space-x-2">
        <select
          value={selectedProductId}
          onChange={(e) => setSelectedProductId(e.target.value)}
          className="flex-1 px-2 py-1.5 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
          disabled={loading}
        >
          <option value="">Select single product...</option>
          {singleProducts.map((product) => (
            <option key={product.id} value={product.id}>
              {product.name} - {product.price} RSD (Stock: {product.quantity})
            </option>
          ))}
        </select>
        <input
          type="number"
          min="1"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          className="w-20 px-2 py-1.5 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-[#264a3e] focus:border-transparent"
          placeholder="Qty"
        />
        <button
          type="button"
          onClick={handleAdd}
          disabled={!selectedProductId || !quantity || loading}
          className="px-3 py-1.5 bg-[#264a3e] text-white rounded text-sm hover:bg-[#1a3329] disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default EditProductModal;
